import * as React from "react";
import {AdmissionCode, Platform} from "@variocube/app-api";
import {EventSupportingApp} from "@variocube/app-common";
import {AppContext, AppErrorBoundary, ThemeProvider} from "@variocube/cube-ui";
import {render} from "react-dom";
import icon from "./delivery.svg";
import {App} from "./app";
import {Item, Rental, RentalState, RentalTypes, Tenant, User} from "./types";
import {DateTime} from "luxon";
import {LocalizationProvider, SUPPORTED_LANGUAGES} from "../i18n";

export default class RentalApp extends EventSupportingApp {

    constructor(p: Platform) {
        super(p, icon, "#9d0aa2", SUPPORTED_LANGUAGES);
    }

    render(root: Element) {
        const appContext = {
            platform: this.platform,
            appEventSupport: this.eventSupport
        };
        const defaultTheme = {
            colorBackground: "#f5e4f6",
            colorForeground: "#381c39",
            colorPrimary: "#9d0aa2",
            colorSeparator: "#56646b",
            colorInputBackground: "#fff",
        }
        render((
            <AppContext.Provider value={appContext}>
                <ThemeProvider defaultTheme={defaultTheme}>
                    <LocalizationProvider app={this}>
                        <AppErrorBoundary appId={this.platform.appId} cubeId={this.platform.hostname}>
                            <App/>
                        </AppErrorBoundary>
                    </LocalizationProvider>
                </ThemeProvider>
            </AppContext.Provider>
        ), root);
    }

    async canProcessAdmissionCode(code: AdmissionCode) {
        console.log('allow processing admission code', code)
        const users = await this.platform.getStorageItem<User[]>('users') ?? [];
        return users.find(u => u.accessCode === code.code);
    }

    async loadMockData() {

        const background = this.platform.getArg("background");

        let backgroundImage = undefined;
        if (background) {
            const backgroundResponse = await window.fetch("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mPccePjfwAIUwOCOf5cIwAAAABJRU5ErkJggg==");
            backgroundImage = await backgroundResponse.blob();
        }

        let occupancy = await this.platform.occupy({
            accessCode: "12345",
            type: "M",
            content: {
                item: "item1",
                rental: "rental1"
            },
        });
        await this.platform.confirmOccupancy(occupancy.uuid);
        console.info("Created occupancy for rental1", occupancy);

        occupancy = await this.platform.occupy({
            accessCode: "12345",
            type: "M",
            content: {
                item: "car1",
                rental: "rental4"
            },
        });
        await this.platform.confirmOccupancy(occupancy.uuid);
        console.info("Created occupancy for rental4", occupancy);

        occupancy = await this.platform.occupy({
            accessCode: "12345",
            type: "M",
            content: {
                item: "car2",
                rental: "rental5"
            },
        });
        await this.platform.confirmOccupancy(occupancy.uuid);
        console.info("Created occupancy for rental5", occupancy);

        occupancy = await this.platform.occupy({
            accessCode: "12345",
            type: "M",
            content: {
                item: "car3",
                rental: "rental6"
            },
        });
        await this.platform.confirmOccupancy(occupancy.uuid);
        console.info("Created occupancy for rental6", occupancy);

        return {
            backgroundImage,
            rentalTypes: "Rental",
            tenant: {
                name: 'ACME Inc',
                email: 'hello@acme.inc',
                phone: '+43 720 / 676 300 – 0',
                web: 'www.variocube.com',
                rentalBaseUrl: "https://rental.variocube.com",
                rentalInfo: {
                    de: "Das ist der Rental-Freitext.",
                    en: "This is the rental free-text.",
                }
            },
            rentals: [
                {
                    uuid: "rental1",
                    state: RentalState.Created,
                    itemUuid: "item1",
                    from: DateTime.local().toISO(),
                    until: DateTime.local().endOf("day").toISO(),
                    borrower: {
                        email: "rick@galactic.co",
                        name: "Rick Sanchez",
                        phone: "+1 888 123456",
                        accessCode: "1",
                        rentalEndsAfterReturn: true,
                    }
                },
                {
                    uuid: "rental2",
                    state: RentalState.Created,
                    itemUuid: "item2",
                    from: DateTime.local().toISO(),
                    until: DateTime.local().endOf("day").toISO(),
                    borrower: {
                        email: "rick@galactic.co",
                        name: "Rick Sanchez",
                        phone: "+1 888 123456",
                        accessCode: "2",
                        accessKey: "4711"
                    }
                },
                {
                    uuid: "rental3",
                    state: RentalState.Created,
                    itemUuid: "item2",
                    from: DateTime.local().endOf("day").toISO(),
                    until: DateTime.local().endOf("day").toISO(),
                    borrower: {
                        email: "rick@galactic.co",
                        name: "Rick Sanchez",
                        phone: "+1 888 123456",
                        accessCode: "3",
                        accessKey: "NzlFRjhEOUQN"
                    }
                },
                {
                    uuid: "rental4",
                    state: RentalState.BorrowerPickedUp,
                    itemUuid: "car1",
                    from: DateTime.local().minus({hours: 2}).endOf("hour").toISO(),
                    until: DateTime.local().plus({days: 1}).endOf("day").toISO(),
                    borrower: {
                        email: "rick@galactic.co",
                        name: "Rick Sanchez",
                        phone: "+1 888 123456",
                        accessCode: "4"
                    },
                    serviceItem: {
                        name: "Ford Mustang Rental4",
                        description: "Ford Mustang from Mr. Sanchez, slight dents in the bumper."
                    },
                    lender: {
                        accessCode: "44"
                    }
                },
                {
                    uuid: "rental5",
                    state: RentalState.LenderPickedUp,
                    itemUuid: "car2",
                    from: DateTime.local().minus({hours: 2}).endOf("hour").toISO(),
                    until: DateTime.local().plus({days: 1}).endOf("day").toISO(),
                    borrower: {
                        email: "rick@galactic.co",
                        name: "Rick Sanchez",
                        phone: "+1 888 123456",
                        accessCode: "5"
                    },
                    serviceItem: {
                        name: "Ford Mustang Rental5",
                        description: "Ford Mustang from Mr. Sanchez, slight dents in the bumper."
                    },
                    lender: {
                        accessCode: "55",
                        pickedUp: DateTime.local().toISO()
                    }
                },
                {
                    uuid: "rental6",
                    state: RentalState.BorrowerPickedUp,
                    itemUuid: "car3",
                    from: DateTime.local().minus({hours: 2}).endOf("hour").toISO(),
                    until: DateTime.local().plus({days: 1}).endOf("day").toISO(),
                    borrower: {
                        email: "rick@galactic.co",
                        name: "Rick Sanchez",
                        phone: "+1 888 123456",
                        accessCode: "6",
                        pickedUp: "yes, indeed!",
                        rentalEndsAfterReturn: true,
                    },
                },
            ],
            items: [
                {
                    uuid: "item1",
                    name: "Portal gun",
                    description: "Super secret weapon",
                    foreignId: "PG-3000"
                },
                {
                    uuid: "item2",
                    name: "Morty",
                    description: "Littler helper",
                    foreignId: "MORTY-384C1"
                },
                {
                    uuid: "item3",
                    name: "Spaceship",
                    description: "Speedy",
                    foreignId: "SPS-Boozecruz69",
                    openCode: "8888888",
                    adminCode: "7777777",
                    accessCodeGenerated: DateTime.local().minus({hours: 2}).endOf("hour").toISO()
                },
                {
                    uuid: "car1",
                    name: "Mini Cooper Black",
                    description: "A black Mini Cooper.",
                    foreignId: "mini-cooper1"
                },
                {
                    uuid: "car2",
                    name: "Mini Cooper Red",
                    description: "A red Mini Cooper.",
                    foreignId: "mini-cooper2"
                }
            ],
            users: [
                {firstName: "Rick", lastName: "Sanchez", accessCode: "999", email: "rick@galactic.co", inspector: true},
                {
                    firstName: "Matthias",
                    lastName: "Steinbauer",
                    accessCode: "111",
                    email: "rick@galactic.co",
                    inspector: false
                }
            ]
        } as MockData;
    }
}

interface MockData {
    tenant?: Tenant;
    rentalTypes: RentalTypes;
    users: User[];
    rentals: Rental[];
    items: Item[];
}
